import { useNavigation } from '@react-navigation/native';
import React, { useCallback } from 'react';
import { StackNavigationProp } from '@react-navigation/stack';
import { addSeconds } from 'date-fns';
import { PixelRatio, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { CARD_DATE_FMT_OPTS, styles } from './sharedCardConfig';
import { useI18n } from '../../context/I18nContext';
import { useMapViewFilter } from '../../context/MapViewFilterContext';
import { useActivePayment } from '../../payment/MapViewActivePaymentContext';
import Hr from '../../components/ui/Hr';
import GradientButton from '../../components/GradientButton';
import { screenNames } from '../../navigators/screenNames';
import { capitalizeFirstLetter, calculateSecurityDepositInCents } from '../../utils/helpers';
import { useNowMinutes } from '../../hooks/useNow';
import { useGetMyThirdPartyAuths, useMyProfileQuery } from '../../apis/appsyncApis';
import ActivePaymentMethodRow from './PaymentMethodRow';
import BookingCardDurationSelector from './BookingCardDurationSelector';
import { PaymentMethodOption, Pod, PricingInfo } from '../../types/misc.types';
import { MapStackRouteParams } from '../../navigators/MapStackNavigator';
import { getData, InternalStorageItemKey } from '../../utils/internalStorage';
import { BillingType, PodStatus, Pois, ProductPaymentType, ThirdPartyAuthProvider } from '../../types/appsync-types';
import Spacer from '../../components/Spacer';
import { PlatformPayButton } from '../../payment/utils/stripe';
import Typography from '../../components/Typography';
import { useGetProductsByPodId } from '../../apis/subscriptionApis';

type Props = {
  space: Pod;
  minBookingMins: number | undefined;
  maxBookingMins: number | undefined;
  isLoading: boolean;
  estimatedPricing: PricingInfo | undefined;
};
type navprops = StackNavigationProp<MapStackRouteParams, 'MapView'>;

function PaymentButtonHelper({ space, disabled, loading }: { space: Pois; disabled: boolean; loading?: boolean }) {
  const navigation = useNavigation<navprops>();
  const myProfileQuery = useMyProfileQuery();
  const { activePaymentMethod } = useActivePayment();
  const { I18n } = useI18n();

  const payButtonOnPressHandler = useCallback(async () => {
    const creditBalance = myProfileQuery.data?.data?.getMyProfile?.creditBalance ?? undefined;
    if (activePaymentMethod || (creditBalance && creditBalance > 0)) {
      const dontShowDepositDialog: boolean = (await getData(InternalStorageItemKey.DONT_SHOW_DEPOSIT_DIALOG)) ?? false;
      const [depositCents, depositCurency] = calculateSecurityDepositInCents(
        space,
        myProfileQuery.data?.data?.getMyProfile,
      );
      if (depositCents && depositCurency && !dontShowDepositDialog) {
        navigation.navigate('SecurityDepositDialog', {
          space,
          amount: depositCents / 100,
          currency: depositCurency,
          showCheckbox: true,
          onOk: 'REPLACE_WITH_CONFIRM',
        });
      } else {
        navigation.navigate('ConfirmReservationDialog', { space });
      }
    } else {
      // If user has no active payment method and no credits,
      // ask them to add a payment method.
      navigation.navigate(screenNames.PaymentChangeBeforeCheckoutView);
    }
  }, [navigation, space, myProfileQuery.data?.data?.getMyProfile, activePaymentMethod]);

  if (
    activePaymentMethod &&
    activePaymentMethod.paymentMethodType === PaymentMethodOption.GOOGLEPAY &&
    space.billingType === BillingType.PREPAID
  ) {
    return <PlatformPayButton disabled={disabled} onPress={payButtonOnPressHandler} style={styles.payButton} />;
  }

  return (
    <GradientButton
      disabled={disabled || !activePaymentMethod}
      loading={loading}
      onPress={payButtonOnPressHandler}
      icon={'credit-card'}
    >
      {I18n.t('mapview.podInfoCard.book')}
    </GradientButton>
  );
}

const BookingContent = ({ space, minBookingMins, maxBookingMins, isLoading, estimatedPricing }: Props) => {
  const { I18n, formatRelative, formatCurrency, formatDuration, formatDateRange, formatDate } = useI18n();
  const now = useNowMinutes();
  const navigation = useNavigation<navprops>();
  const { filter, onChange } = useMapViewFilter();

  const loadingPricing = space.billingType !== BillingType.PAY_AS_YOU_GO ? estimatedPricing === undefined : false;
  // Let's treat space as not being prepaid while data is still loading so the time picker does not pop up
  const isPrepaid =
    !isLoading &&
    space.billingType === BillingType.PREPAID &&
    space.availabilityStatus?.status !== PodStatus.SUBSCRIPTION_ONLY;

  const { activePaymentMethod } = useActivePayment();
  const isUnAvailable =
    minBookingMins !== undefined &&
    maxBookingMins !== undefined &&
    (filter.durationInSecond < minBookingMins * 60 || filter.durationInSecond > maxBookingMins * 60);

  const clockText = isPrepaid
    ? formatDateRange(filter.date, addSeconds(filter.date, filter.durationInSecond), 'time', space.building?.timezone)
    : formatDate(filter.date, 'time', space.building?.timezone);

  let mainText: string;
  if (isUnAvailable) {
    mainText = I18n.t('mapview.podInfoCard.unavailableTitle');
  } else if (space.availabilityStatus?.status === PodStatus.SUBSCRIPTION_ONLY) {
    mainText = I18n.t('mapview.podInfoCard.availableWithSubscription');
  } else {
    mainText = isPrepaid
      ? formatCurrency(estimatedPricing?.price, estimatedPricing?.currency)
      : `${I18n.t('mapview.podInfoCard.available')}`;
  }

  const endDate = isPrepaid ? addSeconds(filter.date, filter.durationInSecond) : undefined;

  const dateText =
    !endDate || filter.date.getUTCDate() == endDate.getUTCDate()
      ? capitalizeFirstLetter(formatRelative(filter.date, now, space.building?.timezone, CARD_DATE_FMT_OPTS))
      : `${capitalizeFirstLetter(
          formatRelative(filter.date, now, space.building?.timezone, CARD_DATE_FMT_OPTS),
        )} - ${capitalizeFirstLetter(formatRelative(endDate, now, space.building?.timezone, CARD_DATE_FMT_OPTS))}`;

  return (
    <>
      <BookingCardDurationSelector
        isPrepaid={isPrepaid}
        pickerText={formatDuration(filter.durationInSecond, 'seconds')}
        pickerMin={minBookingMins}
        pickerMax={maxBookingMins}
        pickerValue={filter.durationInSecond / 60}
        pickerStep={60}
        onValueChange={(newValue) =>
          onChange({
            ...filter,
            durationInSecond: newValue * 60,
          })
        }
        caption={isPrepaid ? `${dateText}\n${clockText}` : undefined}
        mainText={isUnAvailable ? I18n.t('mapview.podInfoCard.unavailableTitle') : mainText}
      />
      <Spacer />
      <Hr />

      <ActivePaymentMethodRow
        paymentMethod={activePaymentMethod}
        onPress={() => navigation.navigate(screenNames.PaymentChangeBeforeCheckoutView)}
      />
      <PaymentButtonHelper disabled={isUnAvailable} loading={loadingPricing} space={space} />
    </>
  );
};

export const AvailableCardContent = ({ space }: Props) => {
  const navigation = useNavigation<navprops>();
  const { I18n } = useI18n();

  const isUnAvailable =
    space.availabilityStatus?.status === PodStatus.UNAVAILABLE ||
    space.availabilityStatus?.status === PodStatus.COMING_SOON ||
    space.availabilityStatus?.status === PodStatus.RESERVED ||
    space.availabilityStatus?.status === PodStatus.RESERVED_BY_OTHER;
  const isSubscriptionOnly = space.availabilityStatus?.status === PodStatus.SUBSCRIPTION_ONLY;

  const getMyThirdPartyAuthsResult = useGetMyThirdPartyAuths();
  const myThirdPartyAuths = getMyThirdPartyAuthsResult.data?.data?.getMyThirdPartyAuths ?? [];

  const getProductsByPodIdResult = useGetProductsByPodId(space.localRef, !isUnAvailable && !!space.localRef);
  const products = getProductsByPodIdResult.data?.data?.getProductsByPodId ?? [];

  const oneTimePaymentProducts = products.filter(
    (product) =>
      product?.paymentType === ProductPaymentType.ONE_TIME || product?.paymentType === ProductPaymentType.RESERVATION,
  );
  const subscriptionProducts = products.filter((product) => product?.paymentType === ProductPaymentType.SUBSCRIPTION);

  const oneTimePaymentsAvailable = oneTimePaymentProducts.length > 0;
  const subscriptionsAvailable = subscriptionProducts.length > 0;

  const firstOneTimePaymentProduct = oneTimePaymentProducts[0];
  const firstSubscriptionProduct = subscriptionProducts[0];

  const authRequiredByOneTimePaymentProducts = firstOneTimePaymentProduct?.requiredAuth ?? undefined;
  const allOneTimePaymentProductsRequireSameAuth = oneTimePaymentProducts.every(
    (product) => (product?.requiredAuth ?? undefined) === authRequiredByOneTimePaymentProducts,
  );
  const allOneTimePaymentProductsRequireAuth =
    allOneTimePaymentProductsRequireSameAuth && authRequiredByOneTimePaymentProducts !== undefined;

  const isUserAuthenticatedForOneTimePaymentProducts = allOneTimePaymentProductsRequireAuth
    ? myThirdPartyAuths.some((auth) => (auth?.authProvider ?? undefined) === authRequiredByOneTimePaymentProducts)
    : true;

  const authRequiredBySubscriptionProducts = firstSubscriptionProduct?.requiredAuth ?? undefined;
  const allSubscriptionProductsRequireSameAuth = subscriptionProducts.every(
    (product) => (product?.requiredAuth ?? undefined) === authRequiredBySubscriptionProducts,
  );
  const allSubsctriptionProductsRequireAuth =
    allSubscriptionProductsRequireSameAuth && authRequiredBySubscriptionProducts !== undefined;

  const isUserAuthenticatedForSubscriptionProducts = allSubsctriptionProductsRequireAuth
    ? myThirdPartyAuths.some((auth) => (auth?.authProvider ?? undefined) === authRequiredBySubscriptionProducts)
    : true;

  const allProductsRequireAuth = allOneTimePaymentProductsRequireAuth && allSubsctriptionProductsRequireAuth;
  const isUserAuthenticatedForAllProducts =
    isUserAuthenticatedForOneTimePaymentProducts && isUserAuthenticatedForSubscriptionProducts;

  const hasLargeFont = PixelRatio.getFontScale() > 2;

  let mainText: string;
  if (isUnAvailable) {
    mainText =
      space.availabilityStatus?.status === PodStatus.RESERVED ||
      space.availabilityStatus?.status === PodStatus.RESERVED_BY_OTHER
        ? I18n.t('mapview.podInfoCard.fullTitle')
        : I18n.t('mapview.podInfoCard.unavailableTitle');
  } else if (isSubscriptionOnly) {
    mainText = I18n.t('mapview.podInfoCard.availableWithSubscription');
  } else {
    mainText = I18n.t('mapview.podInfoCard.available');
  }

  let thirdPartyAuthText: string | undefined;
  if ((allProductsRequireAuth && !isUserAuthenticatedForAllProducts) || !isUserAuthenticatedForOneTimePaymentProducts) {
    thirdPartyAuthText = I18n.t(`thirdPartyAuth.thirdPartyAuthRequired.${firstOneTimePaymentProduct?.requiredAuth}`, {
      defaultValue: I18n.t('thirdPartyAuth.thirdPartyAuthRequired.default', {
        type: I18n.t('thirdPartyAuth.productType.parking'),
      }),
      type: I18n.t('thirdPartyAuth.productType.parking'),
    });
  } else if (!isUserAuthenticatedForSubscriptionProducts) {
    thirdPartyAuthText = I18n.t(`thirdPartyAuth.thirdPartyAuthRequired.${firstSubscriptionProduct?.requiredAuth}`, {
      defaultValue: I18n.t('thirdPartyAuth.thirdPartyAuthRequired.default', {
        type: I18n.t('thirdPartyAuth.productType.subscription'),
      }),
      type: I18n.t('thirdPartyAuth.productType.subscription'),
    });
  }

  return (
    <>
      <View style={[styles.scheduleSection, { flexDirection: 'column', minHeight: 76, justifyContent: 'center' }]}>
        <Typography variant="body1">{mainText}</Typography>
        {!!thirdPartyAuthText && <Typography variant="body1">{thirdPartyAuthText}</Typography>}
      </View>
      {getProductsByPodIdResult.isLoading || getMyThirdPartyAuthsResult.isLoading ? (
        <ActivityIndicator />
      ) : (
        !isUnAvailable && (
          <View style={{ gap: 8 }}>
            {allProductsRequireAuth && !isUserAuthenticatedForAllProducts ? (
              <GradientButton
                icon={{ name: 'shield-account-outline', set: 'material-community' }}
                onPress={() =>
                  navigation.navigate(screenNames.ThirdPartyAuthDetail, {
                    // authRequiredByOneTimePaymentProducts is passed because in this case all products should require the same auth
                    authProvider: authRequiredByOneTimePaymentProducts,
                  })
                }
              >
                {I18n.t('thirdPartyAuth.toThirdPartyAuth')}
              </GradientButton>
            ) : (
              <>
                {oneTimePaymentsAvailable && !isSubscriptionOnly && (
                  <GradientButton
                    icon={
                      isUserAuthenticatedForOneTimePaymentProducts
                        ? undefined
                        : { name: 'shield-account-outline', set: 'material-community' }
                    }
                    onPress={() => {
                      if (isUserAuthenticatedForOneTimePaymentProducts) {
                        if (oneTimePaymentProducts.length === 1) {
                          if (firstOneTimePaymentProduct?.paymentType === ProductPaymentType.RESERVATION) {
                            navigation.navigate(screenNames.ReservationProductDetailScreen, {
                              productId: firstOneTimePaymentProduct?.id,
                            });
                          } else {
                            navigation.navigate(screenNames.OneTimePurchaseProductDetailScreen, {
                              productId: firstOneTimePaymentProduct?.id,
                            });
                          }
                        } else {
                          navigation.navigate(screenNames.OneTimePurchaseProductsScreen, {
                            podId: space.localRef ?? undefined,
                          });
                        }
                      } else {
                        navigation.navigate(screenNames.ThirdPartyAuthDetail, {
                          authProvider: authRequiredByOneTimePaymentProducts!,
                        });
                      }
                    }}
                  >
                    {isUserAuthenticatedForOneTimePaymentProducts
                      ? I18n.t('mapview.podInfoCard.purchaseParking')
                      : I18n.t('thirdPartyAuth.toThirdPartyAuth')}
                  </GradientButton>
                )}

                {subscriptionsAvailable && (
                  <GradientButton
                    icon={
                      isUserAuthenticatedForSubscriptionProducts
                        ? undefined
                        : { name: 'shield-account-outline', set: 'material-community' }
                    }
                    onPress={() => {
                      if (isUserAuthenticatedForSubscriptionProducts) {
                        if (subscriptionProducts.length === 1) {
                          navigation.navigate(screenNames.SubscriptionDetailScreen, {
                            productId: firstSubscriptionProduct?.id,
                          });
                        } else {
                          navigation.navigate(screenNames.SubscriptionsScreen, {
                            podId: space.localRef ?? undefined,
                          });
                        }
                      } else {
                        navigation.navigate(screenNames.ThirdPartyAuthDetail, {
                          authProvider: authRequiredBySubscriptionProducts!,
                        });
                      }
                    }}
                  >
                    {isUserAuthenticatedForSubscriptionProducts
                      ? I18n.t('mapview.podInfoCard.getSubscription')
                      : I18n.t(`thirdPartyAuth.toThirdPartyAuth`)}
                  </GradientButton>
                )}
              </>
            )}
          </View>
        )
      )}
    </>
  );
};
